import { sprintf } from 'utils/helpers';
import {
  JOB_ACTIVITY_TYPES,
  RESPONSE_VARIANTS,
  RANDOM_RESPONSE_VARIANTS,
  ACCEPTED_FILE_EXTENSIONS,
  ACCEPTED_MIME_TYPES,
} from 'components/Chat/RoutingAIChatTypes';

export const isAcceptedFileType = (file) =>
  ACCEPTED_FILE_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext)) ||
  ACCEPTED_MIME_TYPES.includes(file.type);

export const getUploadErrorBody = (status, routingAiChat) => {
  switch (status) {
    case 400:
      return routingAiChat.uploadCsv.error400;
    case 401:
      return routingAiChat.uploadCsv.error401;
    case 413:
      return routingAiChat.uploadCsv.error413;
    case 415:
      return routingAiChat.uploadCsv.error415;
    default:
      return routingAiChat.uploadCsv.error;
  }
};

export const formatMinutesLabel = (minutes, units) => {
  const safeMinutes = Math.max(0, Math.floor(minutes || 0));
  const hours = Math.floor(safeMinutes / 60);
  const leftMinutes = safeMinutes % 60;
  const hourUnit = units?.time?.hour || 'h';
  const minuteUnit = units?.time?.minute || 'm';

  if (hours > 0 && leftMinutes > 0) {
    return `${hours}${hourUnit} ${leftMinutes}${minuteUnit}`;
  }

  if (hours > 0) {
    return `${hours}${hourUnit}`;
  }

  return `${leftMinutes}${minuteUnit}`;
};

export const formatDurationLabel = (durationInSeconds, units) => {
  const totalMinutes = Math.floor((durationInSeconds || 0) / 60);
  return formatMinutesLabel(totalMinutes, units);
};

export const getTourLabel = (tourCount, routingAiChat) =>
  tourCount === 1 ? routingAiChat.tourSingular : routingAiChat.tourPlural;

export const formatWindowTime = (dateString) => {
  if (!dateString) return '--:--';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '--:--';
  return date.toISOString().slice(11, 16);
};

export const getJobTimeWindows = (jobs = []) =>
  jobs.reduce((acc, job) => {
    const deliveryWindows = job.tasks?.deliveries?.[0]?.places?.[0]?.times;
    const pickupWindows = job.tasks?.pickups?.[0]?.places?.[0]?.times;
    const windows = deliveryWindows || pickupWindows || [];

    if (job.id && windows.length > 0) {
      acc[job.id] = windows;
    }

    return acc;
  }, {});

export const findTightestStop = (tours = [], jobs = []) => {
  const jobWindows = getJobTimeWindows(jobs);
  let bestMatch = null;

  tours.forEach((tour, tourIndex) => {
    tour.stops?.forEach((stop, stopIndex) => {
      const arrivalTime = stop.time?.arrival;
      const arrivalMs = Date.parse(arrivalTime);

      if (!arrivalTime || Number.isNaN(arrivalMs)) return;

      stop.activities
        ?.filter(
          ({ type, jobId }) =>
            JOB_ACTIVITY_TYPES.includes(type) && jobId,
        )
        .forEach((activity) => {
          const windows = jobWindows[activity.jobId] || [];

          windows.forEach(([start, end]) => {
            const endMs = Date.parse(end);
            if (Number.isNaN(endMs) || arrivalMs > endMs) return;

            const slackMinutes = Math.floor((endMs - arrivalMs) / 60000);
            if (bestMatch === null || slackMinutes < bestMatch.slackMinutes) {
              bestMatch = {
                slackMinutes,
                stopNumber: stopIndex + 1,
                tourNumber: tourIndex + 1,
                windowStart: start,
                windowEnd: end,
              };
            }
          });
        });
    });
  });

  return bestMatch;
};

export const normalizePromptLabel = (label) => label.replace(/^✓\s*/, '').trim();

export const getRandomResponseVariant = () => {
  const index = Math.floor(Math.random() * RANDOM_RESPONSE_VARIANTS.length);
  return RANDOM_RESPONSE_VARIANTS[index];
};

export const getVariantLabel = (variant, routingAiChat) => {
  switch (variant) {
    case RESPONSE_VARIANTS.READY:
      return routingAiChat.cardLabels.ready;
    case RESPONSE_VARIANTS.WARNING:
      return routingAiChat.cardLabels.headsUp;
    default:
      return routingAiChat.cardLabels.routingAi;
  }
};

export const getMockFollowUpActions = (promptKey, routingAiChat) => {
  switch (promptKey) {
    case 'capacityOverview':
      return [
        { key: 'suggestSwap', label: routingAiChat.cardActions.suggestSwap },
        {
          key: 'addVehicle',
          label: routingAiChat.quickActions.addVehicle,
          secondary: true,
        },
      ];
    case 'truckOffline':
      return [
        { key: 'addVehicle', label: routingAiChat.quickActions.addVehicle },
        {
          key: 'capacityOverview',
          label: routingAiChat.quickActions.capacityOverview,
          secondary: true,
        },
      ];
    case 'applySwap':
      return [
        { key: 'looksGood', label: routingAiChat.cardActions.looksGood },
        {
          key: 'showAlternatives',
          label: routingAiChat.responseActions.showAlternatives,
          secondary: true,
        },
      ];
    case 'showAlternatives':
      return [
        { key: 'applySwap', label: routingAiChat.responseActions.applySwap },
        {
          key: 'looksGood',
          label: routingAiChat.cardActions.looksGood,
          secondary: true,
        },
      ];
    case 'looksGood':
      return [
        { key: 'capacityOverview', label: routingAiChat.quickActions.capacityOverview },
        {
          key: 'truckOffline',
          label: routingAiChat.quickActions.truckOffline,
          secondary: true,
        },
      ];
    case 'fromLibrary':
    case 'oneOff':
    case 'uploadCsv':
      return [
        { key: 'looksGood', label: routingAiChat.cardActions.looksGood },
        {
          key: 'capacityOverview',
          label: routingAiChat.quickActions.capacityOverview,
          secondary: true,
        },
      ];
    default:
      return [
        { key: 'capacityOverview', label: routingAiChat.quickActions.capacityOverview },
        {
          key: 'suggestSwap',
          label: routingAiChat.cardActions.suggestSwap,
          secondary: true,
        },
      ];
  }
};

export const getPromptTitle = (promptKey, fallbackLabel, routingAiChat, summary) => {
  switch (promptKey) {
    case 'suggestSwap':
      if (summary.tightestStop) {
        return sprintf(routingAiChat.promptTitles.suggestSwap, {
          stopNumber: summary.tightestStop.stopNumber,
        });
      }
      return routingAiChat.promptTitles.suggestSwapFallback;
    case 'looksGood':
      return routingAiChat.promptTitles.lockPlan;
    case 'applySwap':
      return routingAiChat.promptTitles.applySwap;
    case 'showAlternatives':
      return routingAiChat.promptTitles.showAlternatives;
    case 'fromLibrary':
      return routingAiChat.promptTitles.fromLibrary;
    case 'oneOff':
      return routingAiChat.promptTitles.oneOff;
    default:
      return normalizePromptLabel(fallbackLabel);
  }
};

export const getResponseCard = ({ promptKey, routingAiChat, summary, units }) => {
  if (!summary.hasPlan && promptKey !== 'generic') {
    return {
      variant: RESPONSE_VARIANTS.ROUTING,
      label: routingAiChat.cardLabels.routingAi,
      body: routingAiChat.noPlanActionReply,
      actions: getMockFollowUpActions(promptKey, routingAiChat),
    };
  }

  switch (promptKey) {
    case 'capacityOverview':
      return {
        variant: RESPONSE_VARIANTS.PLAIN,
        body: sprintf(routingAiChat.capacityCard, {
          assignedOrders: summary.assignedOrders,
          activeFleet: summary.tourCount,
        }),
        actions: getMockFollowUpActions(promptKey, routingAiChat),
      };
    case 'truckOffline':
      return {
        variant: RESPONSE_VARIANTS.WARNING,
        label: routingAiChat.cardLabels.headsUp,
        body: routingAiChat.truckOfflineCard,
        actions: getMockFollowUpActions(promptKey, routingAiChat),
      };
    case 'addVehicle':
      return {
        variant: RESPONSE_VARIANTS.ROUTING,
        label: routingAiChat.cardLabels.routingAi,
        body: routingAiChat.addVehicleCard,
        actions: [
          { key: 'fromLibrary', label: routingAiChat.responseActions.fromLibrary },
          {
            key: 'oneOff',
            label: routingAiChat.responseActions.oneOff,
            secondary: true,
          },
        ],
      };
    case 'fromLibrary':
      return {
        variant: RESPONSE_VARIANTS.ROUTING,
        label: routingAiChat.cardLabels.routingAi,
        body: routingAiChat.fromLibraryReply,
        actions: getMockFollowUpActions(promptKey, routingAiChat),
      };
    case 'oneOff':
      return {
        variant: RESPONSE_VARIANTS.ROUTING,
        label: routingAiChat.cardLabels.routingAi,
        body: routingAiChat.oneOffReply,
        actions: getMockFollowUpActions(promptKey, routingAiChat),
      };
    case 'uploadCsv':
      return {
        variant: RESPONSE_VARIANTS.READY,
        label: routingAiChat.cardLabels.ready,
        body: routingAiChat.uploadCsv.success,
        actions: getMockFollowUpActions(promptKey, routingAiChat),
      };
    case 'suggestSwap': {
      if (!summary.tightestStop) {
        return {
          variant: RESPONSE_VARIANTS.ROUTING,
          label: routingAiChat.cardLabels.routingAi,
          body: routingAiChat.swapReplyNoInsight,
          actions: getMockFollowUpActions(promptKey, routingAiChat),
        };
      }

      return {
        variant: RESPONSE_VARIANTS.WARNING,
        label: routingAiChat.cardLabels.headsUp,
        body: sprintf(routingAiChat.swapCard, {
          stopNumber: summary.tightestStop.stopNumber,
          tourNumber: summary.tightestStop.tourNumber,
          windowStart: formatWindowTime(summary.tightestStop.windowStart),
          windowEnd: formatWindowTime(summary.tightestStop.windowEnd),
          buffer: formatMinutesLabel(summary.tightestStop.slackMinutes, units),
          alternativeStop: Math.max(1, summary.tightestStop.stopNumber - 3),
          improvedBuffer: formatMinutesLabel(summary.tightestStop.slackMinutes + 16, units),
          addedDistance: '1.2',
        }),
        actions: [
          { key: 'applySwap', label: routingAiChat.responseActions.applySwap },
          {
            key: 'showAlternatives',
            label: routingAiChat.responseActions.showAlternatives,
            secondary: true,
          },
        ],
      };
    }
    case 'applySwap':
      return {
        variant: RESPONSE_VARIANTS.READY,
        label: routingAiChat.cardLabels.ready,
        body: sprintf(routingAiChat.applySwapReply, {
          stopNumber: summary.tightestStop?.stopNumber || 1,
          tourNumber: summary.tightestStop?.tourNumber || 1,
        }),
        actions: getMockFollowUpActions(promptKey, routingAiChat),
      };
    case 'showAlternatives':
      return {
        variant: RESPONSE_VARIANTS.ROUTING,
        label: routingAiChat.cardLabels.routingAi,
        body: routingAiChat.showAlternativesReply,
        actions: getMockFollowUpActions(promptKey, routingAiChat),
      };
    case 'looksGood':
      return {
        variant: RESPONSE_VARIANTS.READY,
        label: routingAiChat.cardLabels.ready,
        body: routingAiChat.looksGoodReply,
        actions: getMockFollowUpActions(promptKey, routingAiChat),
      };
    default:
      return {
        variant: RESPONSE_VARIANTS.ROUTING,
        label: routingAiChat.cardLabels.routingAi,
        body: summary.hasPlan ? routingAiChat.genericReply : routingAiChat.noPlanYet,
        actions: getMockFollowUpActions(promptKey, routingAiChat),
      };
  }
};
